import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard, Users, Car, Navigation, CreditCard,
  Tag, DollarSign, Layers, Headphones, Gift, FileText, Settings, X,
  ShieldAlert, Image, Bell, Truck, WalletCards, ChartPie, IndianRupee, Wrench
} from 'lucide-react';
import clsx from 'clsx';
import logo from '../../assets/logo.png';
import { useAppConfigModal } from '../../context/AppConfigModalContext';
import { getSupportUnreadCount } from '../../api/support';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/earnings', icon: IndianRupee, label: 'Earnings' },
  { to: '/users', icon: Users, label: 'Users' },
  { to: '/drivers', icon: Car, label: 'Drivers' },
  { to: '/rides', icon: Navigation, label: 'Rides' },
  { to: '/payments', icon: CreditCard, label: 'Payments' },
  { to: '/wallet-recharges', icon: WalletCards, label: 'Wallet Top-ups' },
  { to: '/collections', icon: ChartPie, label: 'Collections' },
  { to: '/promo-codes', icon: Tag, label: 'Promo Codes' },
  { to: '/vehicle-types', icon: Truck, label: 'Vehicle Types' },
  { to: '/fare-config', icon: DollarSign, label: 'Fare Config' },
  { to: '/plans', icon: Layers, label: 'Plans' },
  { to: '/support', icon: Headphones, label: 'Support' },
  { to: '/offers', icon: Gift, label: 'Offers' },
  { to: '/banners', icon: Image, label: 'Banners' },
  { to: '/safety-alerts', icon: ShieldAlert, label: 'Safety Alerts' },
  { to: '/document-types', icon: FileText, label: 'Document Types' },
  { to: '/notifications', icon: Bell, label: 'Notifications' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export default function Sidebar({ isOpen, onClose }) {
  const { open: openAppConfig } = useAppConfigModal();

  const { data: supportUnread } = useQuery({
    queryKey: ['support', 'unread-count'],
    queryFn: () => getSupportUnreadCount().then((r) => r.data),
    refetchInterval: 15000,
  });
  const supportUnreadCount = supportUnread?.unread_count ?? 0;

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={clsx(
          'fixed left-0 top-0 z-30 h-full w-64 bg-white dark:bg-gray-900',
          'border-r border-gray-200 dark:border-gray-700 flex flex-col',
          'transition-transform duration-300 ease-in-out',
          'lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2.5">
            <img src={logo} alt="VandiGo" className="h-9 w-9 rounded-xl shrink-0" />
            <div>
              <span className="text-base font-bold text-gray-900 dark:text-gray-100">VandiGo</span>
              <p className="text-xs text-gray-400 dark:text-gray-500 leading-none">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                clsx('sidebar-link', isActive && 'active')
              }
            >
              <Icon size={18} />
              <span className="flex-1">{label}</span>
              {to === '/support' && supportUnreadCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white leading-none">
                  {supportUnreadCount > 99 ? '99+' : supportUnreadCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="shrink-0 px-3 py-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-center text-gray-400 dark:text-gray-600">v1.0.0 · Vandigo Admin</p>
        </div>
      </aside>
    </>
  );
}
